import CheckoutAPI from '@/api/checkout'
import CheckoutV2API from '@/api/v2/checkout'
import CardAPI from '@/api/card'
import CHECKOUT_STEPS from '@/constants/checkout-step'
import FraudCheckCodes from '@/constants/fraud-check-codes'
import VerificationSteps from '@/constants/verification-steps'
import Seon from '@/utils/seon'
import { toString } from '@/utils/string'
import router from '@/plugins/router'
import clientLogger from '@/plugins/logger/core'
import logger from '@/utils/logger'
import { redirectToKycPage } from '@/utils/kyc'
import PAYMENT_TYPES from '@/constants/payment-types'
import PAYMENT_BREAKDOWN_TYPES from '@/constants/payment-breakdown-types'
import PAYMENT_BREAKDOWN_SUB_TYPES from '@/constants/payment-breakdown-sub-types'
import PLAN_TYPE from '@/constants/plan-type'
import SUBSCRIPTION_TIERS from '@/constants/subscription-tiers'
import * as Auth from '@/utils/auth'
import { ApplicationError, HttpRequestError } from '@/utils/error'
import Storage from '@/utils/storage'
import isEmpty from 'lodash/isEmpty'
import ORDER_STATUS from '@/constants/order-status'
import OVERDUE_STEPS from '@/constants/overdue-steps'
import EARLY_SETTLEMENT_STEPS from '@/constants/early-settlement-steps'
import HIGHER_DOWN_PAYMENT_OFFERING_VARIANTS from '@/constants/higher-down-payment-offering-variants'
import { getErrorSubtitle, handleNewBackendError } from '@/utils/error-message'
import * as mobileLib from '@/utils/mobile'
import i18n from '@/plugins/i18n'

const STORAGE_KEY_USER_HAS_TRIED_TO_PAY = 'userHasTriedToPay'

const CHECKOUT_REQUEST_RESULT_OK = 0
const CHECKOUT_REQUEST_RESULT_FAILED = 1
const CHECKOUT_REQUEST_RESULT_TOKENS_FAILED = 2
const CHECKOUT_REQUEST_RESULT_NONE = 3

// Private helper
const redirectToNotFoundPage = () => {
  const { query: currentQuery = {} } = router || {}

  router.replace({
    name: '404',
    query: { ...currentQuery, redirectFrom: router.path },
  })
}

const redirectToIvrPage = () => {
  setTimeout(() => {
    const result = router.replace({
      name: 'ivr',
      query: router?.currentRoute?.query,
    })
    if (result && typeof result.catch === 'function') {
      result.catch((err) => console.error('navigation failed:', err))
    }
  }, 0)
}

/**
 * Refreshes card list when payment fails with a 400 error.
 * This helps get updated CVV requirements if the backend has enforced new requirements
 * due to decline attempts.
 */
const handlePaymentErrorCardRefresh = (
  error,
  dataToBeSent,
  dispatch,
  rootGetters,
  rootState
) => {
  // Only proceed for 400 errors
  const responseStatus = error?.response?.status
  if (responseStatus !== 400) return

  // Refresh card data to get latest CVV requirements
  dispatch('payment/allowGetExistingCards', null, { root: true })
  dispatch('payment/getPaymentExistingCards', null, { root: true })
}

const state = {
  data: {},
  fetching: false,
  submitting: false,
  submittingSubscription: false,
  declinedMsg: '',
  failedCode: '',
  viewedPaymentLinkLandingPage: false,
  userHasTriedToPay: false,
  submitExtraFields: [],
  isPaymentMethodSelectionPage: false,
  checkoutRequestResult: CHECKOUT_REQUEST_RESULT_NONE,
  tokenizingCard: {},
}

const getters = {
  currentStep: (state) => {
    return state.data.step
  },
  currentStatus: (state) => {
    return state.data.status
  },
  currentData: (state) => {
    return state.data || {}
  },
  order: (_, getters) => {
    return getters.currentData.order || {}
  },
  customerName(_, getters) {
    return (
      getters.order?.consumer?.firstName +
      ' ' +
      getters.order?.consumer?.lastName
    )
  },
  orderId: (_, getters) => {
    return getters.order.orderId
  },
  loading: (state) => state.fetching,
  submitting: (state) => state.submitting,
  paymentPlan(_, getters) {
    return getters.currentData.paymentPlan || {}
  },
  downpayment(_, getters) {
    return getters.paymentPlan.downpayment || {}
  },
  repayments(_, getters) {
    return getters.paymentPlan?.repayments || []
  },
  walletDownpayment(_, getters) {
    return getters.paymentPlan?.walletDownpayment || {}
  },
  rewardDownpayment(_, getters) {
    return getters.paymentPlan?.rewardDownpayment || {}
  },
  customerServiceFee(_, getters) {
    return (
      getters.paymentPlan?.customerServiceFee ?? {
        amount: 0,
        currency: 'SAR',
      }
    )
  },
  customerServiceFeePercentage(_state, getters, _rootState, rootGetters) {
    const orderTotalAmount = rootGetters['session/orderTotalAmount']

    return Math.floor(
      (getters.customerServiceFee.amount / orderTotalAmount?.amount) * 100
    )
  },
  hasCustomerServiceFee(_, getters) {
    return (getters.paymentPlan?.customerServiceFee?.amount ?? 0) > 0
  },
  rewardDownpaymentPaid(_, getters) {
    return getters.currentData?.rewardDownpaymentPaid || false
  },
  isAmountBelowCsfThreshold(_, getters) {
    return getters.paymentPlan?.isAmountBelowCsfThreshold ?? false
  },
  isWaivedOff(_, getters) {
    return getters.paymentPlan?.isWaivedOff ?? false
  },
  waiveOffCustomerServiceFee(_, getters) {
    return getters.paymentPlan?.waiveOffCustomerServiceFee ?? {}
  },
  hasWaiveOffCustomerServiceFee(_, getters) {
    return (getters.paymentPlan?.waiveOffCustomerServiceFee?.amount ?? 0) > 0
  },
  declinedCode(_, getters) {
    return (
      getters.currentData.declinedCode ||
      getters.currentData?.data?.responseCode ||
      ''
    )
  },
  declinedData(_, getters) {
    return getters.currentData?.declinedData || {}
  },
  declinedMsg(state) {
    return state.declinedMsg || ''
  },
  failedCode(state) {
    return state.failedCode ?? ''
  },
  hasFailedCode(_, getters) {
    return getters.failedCode && getters.failedCode !== ''
  },
  isHighDemographic(_, getters) {
    return getters.currentData.isHighDemographic
  },
  // Payment link only
  paymentOptions(_, getters) {
    return getters.currentData.paymentOptions || []
  },
  isCashbackEnabled(_, getters) {
    return getters.currentData.isCashbackEnabled || false
  },
  isLateFeeEnabled(_, getters) {
    return !!getters.currentData.isLateFeeEnabled
  },
  cashbackPercentage(_, getters) {
    return getters.currentData.cashbackPercentage || 0
  },
  payNowConfigs(_, getters) {
    return getters.currentData?.payNowConfigs || {}
  },
  configByPaymentType(_, getters) {
    const payNowConfigs = getters.currentData?.payNowConfigs || {}
    const cashbackDetails = getters.currentData?.cashbackDetails || {}

    const payNowCashback = cashbackDetails?.breakdown?.find(
      ({ paymentType, type, subType }) =>
        paymentType === PAYMENT_TYPES.PAY_NOW &&
        type === PAYMENT_BREAKDOWN_TYPES.CAMPAIGN &&
        subType === PAYMENT_BREAKDOWN_SUB_TYPES.PAY_IN_FULL
    )

    return {
      [PAYMENT_TYPES.PAY_NOW]: {
        cvvDisabled: payNowConfigs.payNowCvvDisabled || false,
        cashbackValue: payNowCashback?.actualAmount?.amount || 0,
        cashbackPercentage: payNowCashback?.percent || 0,
      },
    }
  },
  allowBackPrevStep(_, getters) {
    return getters.currentData.allowBackPrevStep || false
  },
  lockBackPrevStep(_, getters) {
    return getters.currentData.lockBackPrevStep || false
  },
  lastUsedPaymentMethod(_, getters) {
    return getters.currentData.lastPaymentMethod || ''
  },
  lastUsedCard(_, getters) {
    return getters.currentData.lastCardId || ''
  },
  isNafathEnabled(_, getters) {
    return getters.currentData.isNafathEnabled || false
  },
  isSkipNafathEnabled(_, getters) {
    return getters.currentData.isSkipNafathEnabled || false
  },
  nafathTransaction(_, getters) {
    return getters.currentData.nafathRequest || {}
  },
  shouldCallIvr(_, getters) {
    return getters.currentData.shouldCallIvr ?? false
  },
  ivrRejectionFlowEnabled(_, getters) {
    return getters.currentData.ivrRejectionFlowEnabled ?? false
  },
  ivrStatus(_, getters) {
    return getters.currentData.ivrStatus ?? ''
  },
  idNumber(_, getters) {
    const idNumberFromNafath = getters.nafathTransaction?.idNumber ?? ''

    if (idNumberFromNafath !== '') {
      return idNumberFromNafath
    }

    return getters.currentData?.data?.idNumber ?? ''
  },
  wasSuccessful(_, getters) {
    return [
      ORDER_STATUS.SUCCESS,
      ORDER_STATUS.APPROVED,
      ORDER_STATUS.ON_HOLD,
      ORDER_STATUS.FULL_CAPTURED,
      ORDER_STATUS.AUTHORISED,
    ].includes(getters.currentStatus)
  },
  isFinishStep(_, getters) {
    return getters.currentStep === CHECKOUT_STEPS.FINISH
  },
  isFinishSuccessfuly(_, getters) {
    return getters.isFinishStep && getters.wasSuccessful
  },
  fixedServiceFee(_, getters) {
    return (
      getters.paymentPlan?.downpayment?.customerServiceFee?.fixedServiceFee ?? {
        amount: 0,
        currency: 'SAR',
      }
    )
  },
  hasFixedServiceFee(_, getters) {
    return (getters.fixedServiceFee?.amount ?? 0) > 0
  },
  redirectingToKyc(_, getters) {
    return getters.currentData?.redirectingToKyc ?? false
  },
  isKycStep(_, getters) {
    return [
      CHECKOUT_STEPS.id_verification_sau,
      CHECKOUT_STEPS.id_verification_sau_confirm,
      CHECKOUT_STEPS.id_verification_are,
      CHECKOUT_STEPS.id_verification_nafath_waiting,
      CHECKOUT_STEPS.id_verification_routing,
      CHECKOUT_STEPS.ID_VERIFICATION_UQUDO_INSTANT_APP_LINK,
      CHECKOUT_STEPS.ID_VERIFICATION_UAE_KYC,
    ].includes(getters.currentStep)
  },
  merchantFailureUrl(_, getters) {
    return getters.order?.merchantUrl?.failure || ''
  },
  isSkippedNafath(_, getters) {
    return !!getters.currentData.isSkippedNafath ?? false
  },
  subscriptionDetails(_, getters) {
    return getters.currentData?.subscriptionDetails || {}
  },
  hasSubscription(_, getters) {
    if (getters.isBasicTier) {
      return false
    }

    return getters.subscriptionDetails?.hasSubscription ?? false
  },
  currentSubscription(_, getters) {
    return getters.subscriptionDetails?.currentSubscription || {}
  },
  subscriptionTier(_, getters) {
    return getters.currentSubscription?.tier || {}
  },
  subscriptionTierPlanType(_, getters) {
    return getters.subscriptionTier?.planType || PLAN_TYPE.ACCELERATED
  },
  availableTiers(_, getters) {
    return getters.subscriptionDetails?.availableTiers || []
  },
  availableTier(_, getters) {
    return getters.availableTiers.length > 0 ? getters.availableTiers[0] : {}
  },
  availableTierPlanType(_, getters) {
    return getters.availableTier?.planType || PLAN_TYPE.ACCELERATED
  },
  availableTierFeeFormated(_, getters) {
    return Number(
      getters.hasSubscription
        ? getters.subscriptionTier?.fee ?? 0
        : getters.availableTier?.fee ?? 0
    ).toFixed(2)
  },
  optedInSubscriptionTier(_, getters) {
    return getters.currentData?.optedInSubscriptionTier || {}
  },
  optedInSubscriptionTierPlanType(_, getters) {
    return getters.optedInSubscriptionTier?.planType || PLAN_TYPE.ACCELERATED
  },
  isCustomerOptedInToAvailableTier(_, getters) {
    if (isEmpty(getters.optedInSubscriptionTier)) {
      return false
    }

    if (getters.hasSubscription) {
      return (
        getters.optedInSubscriptionTier?.id === getters.subscriptionTier?.id
      )
    }

    return getters.optedInSubscriptionTier?.id === getters.availableTier?.id
  },
  isOptedInOrSubscribed(_, getters) {
    return getters.isCustomerOptedInToAvailableTier || getters.hasSubscription
  },
  isEligibleToSubcribe(_, getters) {
    return getters.subscriptionDetails?.isEligible ?? false
  },
  isBasicTier(_, getters) {
    return getters.subscriptionTier?.tierCode === SUBSCRIPTION_TIERS.BASIC
  },
  subscriptionTierLogo(_, getters) {
    if (getters.hasSubscription) {
      return getters.subscriptionTier?.logoUrl
    }

    return getters.availableTier?.logoUrl
  },
  subscriptionTierLogoWithName(_, getters) {
    if (getters.hasSubscription) {
      return getters.subscriptionTier?.logoWithNameUrl
    }

    return getters.availableTier?.logoWithNameUrl
  },
  cashbackDetails(_, getters) {
    return getters.currentData?.cashbackDetails || {}
  },
  userHasTriedToPay(state) {
    return state.userHasTriedToPay
  },
  expectedAmount(_, getters) {
    return getters.currentData?.expectedAmount || {}
  },
  cfsRollOutEnabled(_, getters) {
    return getters.currentData?.cfsRollOutEnabled ?? false
  },
  higherDownPaymentOffering(_, getters) {
    return (
      getters.currentData?.higherDownPaymentOffering ??
      HIGHER_DOWN_PAYMENT_OFFERING_VARIANTS.CONTROL
    )
  },
  orderHasEqualInstallments(_, getters) {
    try {
      const paymentType = getters?.order?.paymentType ?? ''

      if (paymentType !== PAYMENT_TYPES.PAY_BY_INSTALMENTS) {
        return false
      }

      const paymentOptions = getters?.paymentOptions ?? []
      const paymentOption = paymentOptions.find(
        (paymentOption) =>
          paymentOption.instalments === (getters?.order?.instalments ?? -1)
      )

      if (!paymentOption) {
        return false
      }

      const downPayment =
        paymentOption?.descriptionBody?.descriptionBodyDownPayment?.amount ?? -1

      if (downPayment < 0) {
        return false
      }

      const repayments = (
        paymentOption?.descriptionBody?.descriptionBodyRepaymentDetails ?? []
      ).map((details) => details?.amount?.amount ?? -1)

      if (repayments.length === 0) {
        return false
      }

      for (let i = 0; i < repayments.length; i++) {
        if (downPayment !== repayments[i]) {
          return false
        }
      }

      return true
    } catch (error) {
      logger.error(error)

      return false
    }
  },
  isMerchantSkipPaymentPlanPreviewPage(_, getters) {
    return getters.currentData?.isMerchantSkipPaymentPlanPreviewPage ?? false
  },
  isEvaluateOrderCreditStep(_, getters) {
    return (
      getters.currentStep === CHECKOUT_STEPS.EVALUATE_ORDER_CREDIT ||
      getters.currentStep === CHECKOUT_STEPS.REATTEMPT_EVALUATE_ORDER_CREDIT
    )
  },
  isProcessingStep(_, getters) {
    return (
      getters.currentStep === CHECKOUT_STEPS.EVALUATE_ORDER_CREDIT ||
      getters.currentStep === CHECKOUT_STEPS.payment_options_selection
    )
  },
  isEarlySettlementConfirmationStep(_, getters) {
    return getters.currentStep === CHECKOUT_STEPS.EARLY_SETTLEMENT_CONFIRMATION
  },
  isEarlySettlementStep(_, getters) {
    return (
      getters.currentStep === EARLY_SETTLEMENT_STEPS.CONFIRMATION ||
      getters.currentStep === EARLY_SETTLEMENT_STEPS.CHARGE_PAYMENTS
    )
  },
  earlySettlementDetails(_, getters) {
    return getters.currentData?.earlySettlementDetails || {}
  },
  earlySettlementTotalAmount(_, getters) {
    return getters.earlySettlementDetails?.total ?? 0
  },
  showChangePlanButton(_, getters) {
    return getters.currentData?.showChangePlanButton ?? false
  },
  isOverDuesConfirmationStep(_, getters) {
    return getters.currentStep === CHECKOUT_STEPS.OVERDUES_CONFIRMATION
  },
  isOverDuesStep(_, getters) {
    return (
      getters.currentStep === OVERDUE_STEPS.CONFIRMATION ||
      getters.currentStep === OVERDUE_STEPS.CHARGE_PAYMENTS
    )
  },
  overduesDetails(_, getters) {
    return getters.currentData?.overduesDetails || {}
  },
  overDuesTotalAmount(_, getters) {
    return getters.overduesDetails?.total ?? 0
  },
  isPendingPaymentsStep(_, getters) {
    return getters.isOverDuesStep || getters.isEarlySettlementStep
  },
  pendingPaymentsTotalAmount(_, getters) {
    if (getters.isOverDuesStep) {
      return getters.overDuesTotalAmount
    }

    return getters.earlySettlementTotalAmount
  },
  financePaymentOptions(_, getters) {
    return getters.currentData?.paymentOptions ?? []
  },
  checkoutRequestResult: (state) => {
    return state.checkoutRequestResult ?? CHECKOUT_REQUEST_RESULT_NONE
  },
  checkoutRequestResultIsOk: (state) => {
    return state.checkoutRequestResult === CHECKOUT_REQUEST_RESULT_OK
  },
  tokenizationFailed: (state) => {
    return state.checkoutRequestResult === CHECKOUT_REQUEST_RESULT_TOKENS_FAILED
  },
  tokenizingCard: (state) => {
    return state.tokenizingCard ?? {}
  },
}

const mutations = {
  UPDATE_DATA: (state, newData) => {
    state.data = newData
  },
  UPDATE_FETCHING(state, value) {
    state.fetching = value
  },
  UPDATE_SUBMITTING(state, value) {
    state.submitting = value
  },
  UPDATE_SUBMITTING_SUBSCRIPTION(state, value) {
    state.submittingSubscription = value
  },
  UPDATE_DECLINED_MSG: (state, declinedMsg) => {
    state.declinedMsg = declinedMsg
  },
  UPDATE_FAILED_CODE: (state, failedCode) => {
    state.failedCode = failedCode
  },
  SET_VIEWED_PAYMENT_LINK_LANDING_PAGE: (state, value) => {
    state.viewedPaymentLinkLandingPage = value
  },
  CLEAR_STATUS(state) {
    if (state.data.status) {
      state.data.status = ''
    }
  },
  CLEAR_DECLINED_CODE(state) {
    state.data.declinedCode = ''

    if (state.data.data) {
      state.data.data.responseCode = ''
    }
  },
  CLEAR_FAILED_CODE(state) {
    state.failedCode = ''
  },
  UPDATE_USER_HAS_TRIED_TO_PAY(state, value) {
    state.userHasTriedToPay = value
  },
  ADD_CHECKOUT_SUBMIT_EXTRA_FIELD(state, options = {}) {
    state.submitExtraFields.forEach((extraFieldOptions, i) => {
      const key = extraFieldOptions?.key ?? ''

      if (key === options.key) {
        delete state.submitExtraFields[i]
      }
    })

    state.submitExtraFields = (state.submitExtraFields ?? []).concat(options)
  },
  UPDATE_IS_PAYMENT_METHOD_SELECTION_PAGE(state, value) {
    state.isPaymentMethodSelectionPage = value
  },
  UPDATE_CHECKOUT_REQUEST_RESULT(state, value) {
    state.checkoutRequestResult = value
  },
  UPDATE_TOKENIZING_CARD(state, tokenizingCard) {
    state.tokenizingCard = tokenizingCard
  },
}

const actions = {
  // Automatic action
  init({ commit, dispatch }) {
    const checkoutId = Auth.getCheckoutId()
    const latestData = Storage.getLatestData() || {}

    if (latestData[checkoutId]) {
      commit('UPDATE_DATA', latestData[checkoutId])
    }

    const landingPageData = Storage.getViewedPaymentLinkLandingPage() || {}

    if (landingPageData[checkoutId]) {
      commit('SET_VIEWED_PAYMENT_LINK_LANDING_PAGE', true)
    }

    let userHasTriedToPay = false
    try {
      userHasTriedToPay = JSON.parse(
        Storage.getSessionKey(STORAGE_KEY_USER_HAS_TRIED_TO_PAY, false) ?? false
      )

      if (typeof userHasTriedToPay !== 'boolean') userHasTriedToPay = false

      commit('UPDATE_USER_HAS_TRIED_TO_PAY', userHasTriedToPay)
    } catch (error) {
      logger.error(error)
    }

    dispatch('clearLatestData')
  },
  async paymentOptionsGeneration({ dispatch }, { checkoutId, payload }) {
    const result = await CheckoutAPI.paymentOptionsGeneration(
      checkoutId,
      payload
    )

    const data = result?.data ?? {}

    dispatch('saveData', data)

    return data
  },
  async submitPaymentOptionNova({ dispatch }, { checkoutId, payload }) {
    const result = await CheckoutAPI.submitPaymentOptionNova(
      checkoutId,
      payload
    )

    const data = result?.data ?? {}

    dispatch('saveData', data)

    return data
  },
  async retrieveDocumentUrl({ dispatch }, { checkoutId, documentId }) {
    const result = await CheckoutAPI.retrieveDocumentUrl(checkoutId, documentId)

    return result?.data ?? {}
  },
  async getResponseCode({ dispatch }, { checkoutId, sessionId }) {
    try {
      if (!sessionId) {
        return
      }

      const result = await CheckoutAPI.getResponseCode(checkoutId, sessionId)
      const message = result?.data?.message
      let responseCode = result?.data?.responseCode

      if (responseCode) {
        if (typeof responseCode === 'number') {
          responseCode = responseCode + ''
        }

        dispatch('checkout/setCheckoutFailedCode', responseCode, { root: true })
      }

      if (message) {
        dispatch('checkout/setCheckoutFailedMessage', message, { root: true })
      }
    } catch (error) {
      logger.error(error)
    }
  },
  async fetchData({ commit, dispatch, getters, rootGetters }, options = {}) {
    const origin = options?.origin ?? 'unknown'

    const v2Route = options?.v2Route ?? false

    clientLogger.debug(`fetchData - ${origin}`)

    if (getters.currentStep === 'finish') {
      return
    }

    const checkoutId = rootGetters.checkoutId
    const isEmbedded = rootGetters['application/isEmbedded']

    try {
      if (!checkoutId) {
        redirectToNotFoundPage()

        throw new ApplicationError('checkout_id is required!')
      }

      commit('UPDATE_FETCHING', true)

      const fetchData = rootGetters['session/isInStore']
        ? CheckoutV2API.fetchData
        : CheckoutAPI.fetchData
      const response = await fetchData({
        checkoutId,
        isFrame: isEmbedded,
        v2Route,
      })

      commit('UPDATE_CHECKOUT_REQUEST_RESULT', CHECKOUT_REQUEST_RESULT_OK)

      const { data } = response || {}

      if (
        getters.currentStep === 'finish' &&
        [
          ORDER_STATUS.SUCCESS,
          ORDER_STATUS.APPROVED,
          ORDER_STATUS.FULL_CAPTURED,
          ORDER_STATUS.AUTHORISED,
        ].includes(data?.status ?? '')
      ) {
        mobileLib.sendMessage(
          rootGetters['checkout/orderId'],
          'instore_checkout_success',
          'view_order_details'
        )
      }

      // Update installments in sessions for payment link - Because the payment link is updated payment type after create create session
      const { instalments, paymentType } = data?.order || {}

      if (instalments) {
        dispatch('session/updateInstallments', instalments, { root: true })
      }

      if (paymentType) {
        dispatch('session/updatePaymentType', paymentType, { root: true })
      }

      commit('UPDATE_FETCHING', false)

      dispatch('saveData', data)

      if (data?.submittingNps) {
        dispatch('merchant/requireNps', true, { root: true })
      }

      // Single Checkout: get profile data after id verification
      const isIdVerified = rootGetters['auth/isIdVerified']
      const singleCheckoutRevampEnabled =
        rootGetters['session/singleCheckoutRevampEnabled']

      const idVerificationSteps = Object.values(VerificationSteps).includes(
        data?.step
      )
      if (
        (!isIdVerified || idVerificationSteps) &&
        singleCheckoutRevampEnabled
      ) {
        dispatch('auth/getProfile', { checkoutId, force: true }, { root: true })
      }

      if (data?.step === CHECKOUT_STEPS.kyc_processing) {
        redirectToKycPage({ kycSessionId: data.kycSessionId })

        return data
      }
      if (data?.step === CHECKOUT_STEPS.POST_LOGIN_IVR_WAITING) {
        redirectToIvrPage()

        return data
      }

      return data
    } catch (error) {
      console.error('TAMARA >> ERROR >> ', error)

      commit('UPDATE_CHECKOUT_REQUEST_RESULT', CHECKOUT_REQUEST_RESULT_FAILED)

      commit('UPDATE_FETCHING', false)

      const response = error?.response || {}

      if (toString(response?.status) === '404') {
        redirectToNotFoundPage()
      }

      throw error
    }
  },

  async tokenizeCard(
    { commit, dispatch, rootGetters },
    { payload = {}, card = {} }
  ) {
    try {
      commit('UPDATE_TOKENIZING_CARD', card)

      const paymentType = rootGetters['session/paymentType']
      const auditRules =
        paymentType === PAYMENT_TYPES.PAY_BY_INSTALMENTS ? ['prepaid'] : []

      const response = await CardAPI.tokenize({
        ...payload,
        auditRules,
      })

      commit('UPDATE_CHECKOUT_REQUEST_RESULT', CHECKOUT_REQUEST_RESULT_OK)

      return response
    } catch (error) {
      // Extract error details
      const { errorCode, errorMessage, locale } = handleNewBackendError(error)

      const debugMsg = `Failed to tokenize card for checkoutId: ${rootGetters.checkoutId}. Error [${errorCode}]: ${errorMessage}`

      // logging the error
      logger.debug(debugMsg)
      clientLogger.debug(debugMsg)

      const declinedMsg = i18n.t(
        getErrorSubtitle(errorCode, null, errorMessage, locale)
      )
      // Update the Vuex state with the error message
      dispatch('setCheckoutFailedMessage', declinedMsg)

      commit(
        'UPDATE_CHECKOUT_REQUEST_RESULT',
        CHECKOUT_REQUEST_RESULT_TOKENS_FAILED
      )

      if (errorCode) {
        dispatch('checkout/setCheckoutFailedCode', errorCode, {
          root: true,
        })
      }

      throw error
    }
  },

  async submitData(
    { commit, dispatch, getters, rootGetters, rootState, state },
    {
      autoSave = true,
      options,
      origin = 'unknown',
      uri = null,
      omitCheckoutId = false,
      omitData = false,
      useV2 = false,
      ...dataToBeSent
    } = {} // autoSave is a flag that automatically saves data to the store
  ) {
    clientLogger.debug(`submitData - ${origin}`)

    try {
      if (state.submitting) {
        return
      }

      const checkoutId = rootGetters.checkoutId

      if (!checkoutId) {
        redirectToNotFoundPage()

        throw new ApplicationError('checkout_id is required!')
      }

      commit('UPDATE_SUBMITTING', true)

      const sentData = dataToBeSent
      const seonData = Seon.getSeonData() || {}

      if (
        seonData.seonEnabled &&
        seonData.seonSession &&
        !dataToBeSent.id_photo // TODO: need to change for case submit ID data
      ) {
        if (sentData.data) {
          sentData.data.seon_session = seonData.seonSession
        } else {
          sentData.data = { seon_session: seonData.seonSession }
        }
      }

      if (omitData) {
        delete sentData.data
      }

      let response = {}

      const extraFields = buildExtraFields(getters, state)

      if (typeof sentData.data === 'object') {
        sentData.data = { ...sentData.data, ...extraFields }
      }

      if (rootGetters['session/isInStore'] || useV2) {
        response = await CheckoutV2API.submitData({
          checkoutId,
          data: sentData,
          options: { ...options, omitCheckoutId, uri },
        })
      } else {
        response = await CheckoutAPI.submitData({
          checkoutId,
          data: sentData,
          options,
        })
      }

      commit('UPDATE_CHECKOUT_REQUEST_RESULT', CHECKOUT_REQUEST_RESULT_OK)

      const { data } = response || {}

      commit('UPDATE_SUBMITTING', false)

      if (data?.submittingNps) {
        dispatch('merchant/requireNps', true, { root: true })
      }

      // Don't cache data
      if (!autoSave) {
        return { data }
      }

      // Clear response code before save to cache
      const { responseCode, ...restData } = data?.data || {}

      if (state.data.actionRequired === 'resubmit' && origin === 'resubmit') {
        // BE wanted a resubmit, and we have just done it, now unset the field before saving
        // This will prevent us from an infinte loop
        data.actionRequired = data.actionRequired ?? null
      }

      dispatch('saveData', { ...data, data: restData })

      // Single Checkout: get profile data after id verification
      const isIdVerified = rootGetters['auth/isIdVerified']
      const singleCheckoutRevampEnabled =
        rootGetters['session/singleCheckoutRevampEnabled']

      const idVerificationSteps = Object.values(VerificationSteps).includes(
        getters.currentStep
      )
      if (
        (!isIdVerified || idVerificationSteps) &&
        singleCheckoutRevampEnabled
      ) {
        dispatch('auth/getProfile', { checkoutId, force: true }, { root: true })
      }
      if (data?.step === CHECKOUT_STEPS.kyc_processing) {
        redirectToKycPage({ kycSessionId: data.kycSessionId })

        return data
      }

      return { data }
    } catch (error) {
      commit('UPDATE_CHECKOUT_REQUEST_RESULT', CHECKOUT_REQUEST_RESULT_FAILED)

      logger.debug(`Failed to submit ${rootGetters.checkoutId}`, {
        checkoutId: rootGetters.checkoutId,
        orderId: rootGetters['checkout/orderId'],
      })
      clientLogger.debug(`Failed to submit ${rootGetters.checkoutId}`)

      const paymentErrorCode = error?.response?.data?.errors?.[0]?.error_code

      if (paymentErrorCode) {
        dispatch('checkout/setCheckoutFailedCode', paymentErrorCode, {
          root: true,
        })
      }

      const errorCode = HttpRequestError.getErrorCode(error)
      const isFraudCheckError = !!FraudCheckCodes[errorCode]
      const declinedMsg = i18n.t(getErrorSubtitle(errorCode))

      if ((error?.response?.status ?? null) === 408) {
        dispatch('payment/setFailedTimeout', true, { root: true })
      }

      if (isFraudCheckError) {
        dispatch('payment/setFraudCheck', errorCode, { root: true })
      }

      commit('UPDATE_SUBMITTING', false)
      // Set declined error message
      dispatch('checkout/setCheckoutFailedMessage', declinedMsg, { root: true })

      const response = error?.response || {}

      if (toString(response?.status) === '404') {
        redirectToNotFoundPage()
      }

      // Handle card refresh for CVV enforcement scenarios
      handlePaymentErrorCardRefresh(
        error,
        dataToBeSent,
        dispatch,
        rootGetters,
        rootState
      )

      throw error
    }
  },
  setViewedPaymentLinkLandingPage({ commit, rootGetters }, flag) {
    const checkoutId = rootGetters.checkoutId

    if (!checkoutId) {
      return
    }

    const viewedPaymentLinkLandingPage = { [checkoutId]: flag }

    commit('SET_VIEWED_PAYMENT_LINK_LANDING_PAGE', flag)

    Storage.setViewedPaymentLinkLandingPage(viewedPaymentLinkLandingPage)
  },
  saveData({ commit, state, rootGetters }, data) {
    const checkoutId = rootGetters.checkoutId

    if (TypeChecker.isObject(data)) {
      const hadCosmosFlow = !!state.data?.isCosmosFlow
      const cosmosFlowEnabled = !!data?.isCosmosFlow

      const newData = { ...state.data, ...data }

      commit('UPDATE_DATA', newData)
      Storage.setLatestData({ [checkoutId]: newData })

      if (cosmosFlowEnabled && !hadCosmosFlow) {
        mobileLib.notifyUseCosmosFlutter(checkoutId)
      }
    }
  },
  setLockBackPrevStep({ dispatch }) {
    dispatch('saveData', { lockBackPrevStep: true })
  },
  setCheckoutFailedMessage({ commit }, message) {
    commit('UPDATE_DECLINED_MSG', message)
  },
  setCheckoutFailedCode({ commit }, code) {
    if (typeof code === 'number') {
      code = code + ''
    }

    commit('UPDATE_FAILED_CODE', code)
  },
  setSubmittingSubscription({ commit }, value) {
    commit('UPDATE_SUBMITTING_SUBSCRIPTION', value)
  },
  clearCheckoutErrorData({ dispatch }) {
    dispatch('clearDeclinedCode')
    dispatch('setCheckoutFailedMessage', '')
    dispatch('setCheckoutFailedCode', '')
  },
  clearSession({ commit, dispatch }) {
    commit('UPDATE_DATA', {})
    commit('UPDATE_FETCHING', false)
    commit('UPDATE_SUBMITTING', false)
    commit('UPDATE_DECLINED_MSG', '')
    commit('UPDATE_FAILED_CODE', '')
    commit('SET_VIEWED_PAYMENT_LINK_LANDING_PAGE', false)

    return dispatch('clearLatestData')
  },

  clearLatestData() {
    Storage.clearLatestData()
  },

  cleanUp({ dispatch }) {
    dispatch('clearSession')
  },
  clearStatus({ getters, dispatch }) {
    const newData = { ...getters.currentData, status: '' }

    dispatch('saveData', newData)
  },
  clearDeclinedCode({ commit }) {
    commit('CLEAR_DECLINED_CODE')
  },
  clearFailedCode({ commit }) {
    commit('CLEAR_FAILED_CODE')
  },
  setUserHasTriedToPay({ commit }) {
    try {
      Storage.setSessionKey(STORAGE_KEY_USER_HAS_TRIED_TO_PAY, true)
    } catch (error) {
      logger.error(error)
    }

    commit('UPDATE_USER_HAS_TRIED_TO_PAY', true)
  },
  addCheckoutSubmitExtraField({ commit }, options = {}) {
    commit('ADD_CHECKOUT_SUBMIT_EXTRA_FIELD', options)
  },
  setIsPaymentMethodSelectionPage({ commit }, value) {
    commit('UPDATE_IS_PAYMENT_METHOD_SELECTION_PAGE', value)
  },
}

const buildExtraFields = (getters, state) => {
  try {
    const extraFields = {}
    ;(state?.submitExtraFields ?? []).forEach((extraFieldOptions = {}) => {
      const currentStep = getters?.currentStep ?? ''
      const filterSteps = extraFieldOptions?.filterSteps ?? []

      if (filterSteps.length > 0 && filterSteps.indexOf(currentStep) === -1)
        return

      const key = extraFieldOptions?.key ?? ''
      const value = extraFieldOptions?.value ?? ''

      if (key === '' || value === '') return

      extraFields[key] = value
    })

    return extraFields
  } catch (err) {
    return {}
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
